import PPUtils from "../../utils/PPUtils";
import RuleEngineBackend from "./RuleEngineBackend";
import { parseDecimalInputOrNull } from "../common/decimalInputConfig";

/**
 * Top-level component JSON keys that the React editor fully models in
 * buildComponentsString(). Any key NOT in this set that exists on a loaded rule
 * must be preserved verbatim on save, otherwise the stored rule gets corrupted
 * and the backend can crash when it tries to read the missing data.
 */
const KNOWN_COMPONENT_KEYS = new Set([
     "channel",
     "productAttributes",
     "own_stock",
     "product_filter",
     "categories",
     "shops",
     "foreignshop_stock",
     "maxPriceAdjustment",
     "priceAdjustments",
     "dateComponent",
     "priceRounding",
     "calculate_by_channel",
     "adjustmentByMarge",
     "amazonBuyBox",
     "useAllPricesFromGroup",
     "checkboxes",
     "creator",
     "createDate"
]);

function isPlainObject(value) {
     return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function toBackendCsv(value, keys = []) {
     if (value === null || value === undefined || value === "") return "";
     const values = Array.isArray(value) ? value : String(value).split(",");
     return values
          .flatMap((entry) => entry?.isAllOption && Array.isArray(entry.allEntries) ? entry.allEntries : [entry])
          .map((entry) => {
               if (entry === null || entry === undefined) return "";
               if (typeof entry !== "object") return String(entry).trim();
               for (const key of keys) {
                    if (entry[key] !== null && entry[key] !== undefined) return String(entry[key]).trim();
               }
               return "";
          })
          .filter(Boolean)
          .join(",");
}

export function csvToSelectOptions(value, valueKey = "catShort") {
     if (Array.isArray(value)) return value;
     if (!value) return [];
     return String(value).split(",").map((entry) => entry.trim()).filter(Boolean).map((entry) => ({
          value: entry,
          label: entry,
          [valueKey]: entry
     }));
}

function backendDatesToHtml(value) {
     if (!value) return "";
     return String(value).split(",").map((date) => {
          const match = date.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
          return match ? `${match[3]}-${match[2]}-${match[1]}` : date.trim();
     }).join(",");
}

function htmlDatesToBackend(value) {
     if (!value) return "";
     return String(value).split(",").map((date) => {
          const match = date.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
          return match ? `${match[3]}-${match[2]}-${match[1]}` : date.trim();
     }).join(",");
}

export function toNullableNumber(value) {
     return parseDecimalInputOrNull(value);
}

function finiteNumberOr(value, fallback = 0) {
     const parsed = Number(value);
     return Number.isFinite(parsed) ? parsed : fallback;
}

function finiteString(value, fallback = "0.0") {
     const parsed = Number(value);
     return Number.isFinite(parsed) ? String(parsed) : fallback;
}

function isMinimumBoundaryValueDisabled(value) {
     if (value === null || value === undefined || value === "") {
          return true;
     }
     return String(value).toLowerCase() === "nan";
}

function parseMinimumBoundaryValue(value) {
     if (isMinimumBoundaryValueDisabled(value)) {
          return null;
     }
     const parsed = Number(value);
     return Number.isFinite(parsed) ? parsed : null;
}

function isMinimumBoundaryEnabledFromComponents(euro, percent) {
     return !isMinimumBoundaryValueDisabled(euro) || !isMinimumBoundaryValueDisabled(percent);
}

function serializeMinimumBoundaryValue(value, enabled) {
     if (!enabled) {
          return "NaN";
     }
     return finiteString(value);
}

function applyMinimumBoundaryFromComponents(helper, euroValue, percentValue) {
     const enabled = isMinimumBoundaryEnabledFromComponents(euroValue, percentValue);
     helper.setMinimumBoundaryEnabled(enabled);
     if (enabled) {
          helper.setMinimumEuroOverEK(parseMinimumBoundaryValue(euroValue) ?? 0);
          helper.setMinimumPercent(parseMinimumBoundaryValue(percentValue) ?? 0);
     }
}

function scalarString(value, fallback = "") {
     if (value === null || value === undefined) return fallback;
     if (typeof value === "object") {
          const scalar = value.value ?? value.label;
          return scalar === null || scalar === undefined ? fallback : String(scalar);
     }
     return String(value);
}

function parseBooleanFlag(value) {
     if (value === true || value === "true") return true;
     if (value === false || value === "false") return false;
     return null;
}

function undercutTypeToIsEuro(undercutType) {
     if (undercutType === "€") return true;
     if (undercutType === "%") return false;
     return null;
}

function resolvePriceAdjustmentIsEuro(adjustment, formulas) {
     const explicit = parseBooleanFlag(adjustment?.isEuro);
     if (explicit !== null) {
          return explicit;
     }

     for (const formula of formulas || []) {
          const fromType = undercutTypeToIsEuro(formula?.undercutType);
          if (fromType !== null) {
               return fromType;
          }
     }

     return false;
}

function finiteFormula(formula) {
     return {
          ...(formula || {}),
          X: finiteNumberOr(formula?.X),
          Y: finiteNumberOr(formula?.Y),
          Z: finiteNumberOr(formula?.Z)
     };
}

function convertSymbolToServerOperator(symbol) {
     if (symbol == null || symbol === "") {
          return null;
     }
     switch (symbol) {
          case "+": return "PLUS";
          case "-": return "MINUS";
          case "*": return "MULTIPLICATION";
          case "/": return "DIVISION";
          case "PLUS":
          case "MINUS":
          case "MULTIPLICATION":
          case "DIVISION":
               return symbol;
          default:
               return null;
     }
}

function buildMargeFormulaPayload(formula) {
     if (!formula) {
          return {};
     }

     const payload = {
          X: finiteString(formula.X),
          Y: finiteString(formula.Y),
          Z: finiteString(formula.Z),
     };

     if (formula.useFactor) {
          payload.useFactor = true;
     }

     const op1 = convertSymbolToServerOperator(formula.OP_1);
     const op2 = convertSymbolToServerOperator(formula.OP_2);
     const op3 = convertSymbolToServerOperator(formula.OP_3);

     if (op1) payload.OP_1 = op1;
     if (op2) payload.OP_2 = op2;
     if (op3) payload.OP_3 = op3;

     return stripInvalidMargeFormulaOperators(payload);
}

function stripInvalidMargeFormulaOperators(formula) {
     if (!isPlainObject(formula)) {
          return formula;
     }

     const result = { ...formula };
     ["OP_1", "OP_2", "OP_3"].forEach((key) => {
          const operator = convertSymbolToServerOperator(result[key]);
          if (operator) {
               result[key] = operator;
          } else {
               delete result[key];
          }
     });

     return result;
}

function sanitizeComponentsForBackend(components) {
     if (!isPlainObject(components)) {
          return components;
     }

     const sanitized = { ...components };

     if (isPlainObject(sanitized.maxPriceAdjustment)) {
          sanitized.maxPriceAdjustment = { ...sanitized.maxPriceAdjustment };
          if (sanitized.maxPriceAdjustment.minimumPrice) {
               sanitized.maxPriceAdjustment.minimumPrice = stripInvalidMargeFormulaOperators(
                    sanitized.maxPriceAdjustment.minimumPrice
               );
          }
          if (sanitized.maxPriceAdjustment.maximumPrice) {
               sanitized.maxPriceAdjustment.maximumPrice = stripInvalidMargeFormulaOperators(
                    sanitized.maxPriceAdjustment.maximumPrice
               );
          }
     }

     if (isPlainObject(sanitized.adjustmentByMarge) && sanitized.adjustmentByMarge.formula) {
          sanitized.adjustmentByMarge = {
               ...sanitized.adjustmentByMarge,
               formula: stripInvalidMargeFormulaOperators(sanitized.adjustmentByMarge.formula),
          };
     }

     return sanitized;
}

const PRICE_ADJUSTMENT_TYPE_ALIASES = {
     AVERAGE_PRICE_OF_THREE_CHEAPEST: "AVERAGE_PRICE",
     AVERAGE_PRICE_OF_ALL: "AVERAGE_PRICE",
     TOP_PRICE: "NEXT_PRICE_ABOVE_MIN_PRICE",
     TOP_PRICE_INCLUDED_PA: "NEXT_PRICE_ABOVE_MIN_PRICE_INCLUSIVE_UNDERCUTTING_VALUE"
};

export function canonicalPriceAdjustmentType(value) {
     return PRICE_ADJUSTMENT_TYPE_ALIASES[value] || value;
}

export function doubleKey(value) {
     const parsed = toNullableNumber(value);
     if (parsed === null) return null;
     return Number.isInteger(parsed) ? parsed.toFixed(1) : String(parsed);
}

/**
 * True for objects whose keys are ALL numeric strings (e.g. price-threshold
 * value maps like {"9999.0":"2. NKS<->9"} or {"4":1}). These are data maps the
 * editor fully rebuilds, NOT structs with unmodeled sub-fields, so they must be
 * taken wholesale from the rebuilt side. Deep-filling them from the raw rule
 * would re-introduce stale keys (e.g. "9999.0" alongside a rebuilt "9999").
 */
function isNumericKeyMap(value) {
     if (!isPlainObject(value)) {
          return false;
     }
     const keys = Object.keys(value);
     if (keys.length === 0) {
          return false;
     }
     return keys.every((key) => /^-?\d+(\.\d+)?$/.test(key));
}

/** Structured deep clone that is safe for the plain JSON rule/component data. */
function deepClone(value) {
     if (value === null || typeof value !== "object") {
          return value;
     }
     try {
          return JSON.parse(JSON.stringify(value));
     } catch (error) {
          return value;
     }
}

/**
 * Returns a copy of `primary` where every key present in `fallback` but missing
 * in `primary` is filled from `fallback` (recursively for plain objects).
 * `primary` always wins for keys it defines; arrays are taken from `primary`
 * as-is and never merged element-wise.
 */
function deepFillMissing(primary, fallback) {
     if (!isPlainObject(primary) || !isPlainObject(fallback)) {
          return primary;
     }
     // Numeric-keyed value maps are fully owned/rebuilt by the editor. Filling
     // missing keys from the raw rule would duplicate stale threshold keys.
     if (isNumericKeyMap(primary) || isNumericKeyMap(fallback)) {
          return { ...primary };
     }
     const result = { ...primary };
     Object.keys(fallback).forEach((key) => {
          if (!(key in result)) {
               result[key] = fallback[key];
          } else if (isPlainObject(result[key]) && isPlainObject(fallback[key])) {
               result[key] = deepFillMissing(result[key], fallback[key]);
          }
     });
     return result;
}

/**
 * Merge freshly rebuilt components onto the original (raw) components loaded from
 * the backend so that:
 *  - unknown top-level keys the editor does not model survive untouched,
 *  - known components are taken from the rebuilt set (so edits AND deletions of
 *    components apply correctly),
 *  - unmodeled sub-keys inside a known component are preserved (rebuilt wins,
 *    raw fills the gaps).
 */
function mergeComponentsPreservingUnknown(rawComponents, rebuiltComponents) {
     if (!isPlainObject(rawComponents)) {
          return rebuiltComponents;
     }
     const result = {};

     Object.keys(rawComponents).forEach((key) => {
          if (!KNOWN_COMPONENT_KEYS.has(key)) {
               result[key] = rawComponents[key];
          }
     });

     Object.keys(rebuiltComponents).forEach((key) => {
          const rebuiltValue = rebuiltComponents[key];
          const rawValue = rawComponents[key];
          if (isPlainObject(rebuiltValue) && isPlainObject(rawValue)) {
               result[key] = deepFillMissing(rebuiltValue, rawValue);
          } else {
               result[key] = rebuiltValue;
          }
     });

     return result;
}

function convertServerRoundingValuesToModalFormat(serverRoundingValues) {
     if (!serverRoundingValues || typeof serverRoundingValues !== 'object') {
          return { formulas: [{ bis: null, nachkommastelle: "", rundungAuf: null }] };
     }

     const formulas = [];

     Object.entries(serverRoundingValues).forEach(([priceLimit, roundingRule]) => {
          const bis = parseFloat(priceLimit);

          if (typeof roundingRule === 'string') {
               const parts = roundingRule.split('<->');
               if (parts.length === 2) {
                    const nachkommastellePart = parts[0].trim();
                    const rundungAuf = parseInt(parts[1]);

                    let nachkommastelle = "";
                    if (nachkommastellePart.includes("1.")) {
                         nachkommastelle = "ERSTE";
                    } else if (nachkommastellePart.includes("2.")) {
                         nachkommastelle = "ZWEITE";
                    }

                    formulas.push({
                         bis: isNaN(bis) ? null : bis,
                         nachkommastelle: nachkommastelle,
                         rundungAuf: isNaN(rundungAuf) ? null : rundungAuf
                    });
               }
          }
     });

     if (formulas.length === 0) {
          formulas.push({ bis: null, nachkommastelle: "", rundungAuf: null });
     }

     return { formulas };
}

function convertModalRoundingValuesToServerFormat(modalRoundingValues) {
     if (!modalRoundingValues?.formulas || !Array.isArray(modalRoundingValues.formulas)) {
          return {};
     }

     const serverFormat = {};

     modalRoundingValues.formulas.forEach(formula => {
          if (formula.bis !== null && formula.nachkommastelle && formula.rundungAuf !== null) {
               const nachkommastelleText = formula.nachkommastelle === "ERSTE" ? "1. NKS" : "2. NKS";
               const roundingValue = formula.nachkommastelle === "ERSTE"
                    ? String(formula.rundungAuf).padStart(2, "0")
                    : String(formula.rundungAuf);
               const roundingRule = `${nachkommastelleText}<->${roundingValue}`;
               const key = doubleKey(formula.bis);
               if (key !== null) serverFormat[key] = roundingRule;
          }
     });

     return serverFormat;
}

function convertServerRoundingTypeToModalFormat(serverType) {
     switch (serverType) {
          case "UP": return "IMMER_AUFRUNDEN";
          case "DOWN": return "IMMER_ABRUNDEN";
          case "UPORDOWN": return "AB_HAELFTE_AUF";
          case "IMMER_AUFRUNDEN": return "IMMER_AUFRUNDEN";
          case "IMMER_ABRUNDEN": return "IMMER_ABRUNDEN";
          case "AB_HAELFTE_AUF": return "AB_HAELFTE_AUF";
          default: return serverType;
     }
}

const STOCK_INDEX_RED = 0;
const STOCK_INDEX_GREEN = 1;
const STOCK_INDEX_YELLOW = 2;

function normalizeStockToBooleans(stockArray) {
     const booleans = [false, false, false];
     if (!Array.isArray(stockArray)) return booleans;

     const containsEnum = stockArray.some((entry) => typeof entry === "string");
     if (!containsEnum) {
          booleans[STOCK_INDEX_RED] = !!stockArray[STOCK_INDEX_RED];
          booleans[STOCK_INDEX_GREEN] = !!stockArray[STOCK_INDEX_GREEN];
          booleans[STOCK_INDEX_YELLOW] = !!stockArray[STOCK_INDEX_YELLOW];
          return booleans;
     }

     stockArray.forEach((entry) => {
          switch (String(entry).toUpperCase()) {
               case "RED": booleans[STOCK_INDEX_RED] = true; break;
               case "GREEN": booleans[STOCK_INDEX_GREEN] = true; break;
               case "YELLOW": booleans[STOCK_INDEX_YELLOW] = true; break;
               default: break;
          }
     });
     return booleans;
}

const BORDER_STYLE_POOL = [
     "border_red", "border_turkis", "border_green", "border_orange",
     "border_lila", "border_pink", "border_blue", "border_yellow",
     "border_bright_red", "border_bright_turkis", "border_bright_green", "border_bright_orange",
     "border_bright_lila", "border_bright_pink", "border_bright_blue", "border_bright_yellow"
];

function pickBorderStyle(rule, usedBorderStyles) {
     const existing = rule.border_style || "";
     // Reuse a pre-existing border_style only if it is still unique among the own
     // stocks emitted so far. Two own stocks must never share a border_style, or the
     // foreign-stock / price-adjustment foreign keys become ambiguous.
     if (existing && !existing.startsWith("border_stock_") && !usedBorderStyles.has(existing)) {
          usedBorderStyles.add(existing);
          return existing;
     }

     const colorByIndex = ["red", "green", "yellow"];
     let preferred = null;
     for (let i = 0; i < colorByIndex.length; i++) {
          if (Array.isArray(rule.stock) && rule.stock[i]) {
               preferred = "border_" + colorByIndex[i];
               break;
          }
     }
     if (preferred && BORDER_STYLE_POOL.includes(preferred) && !usedBorderStyles.has(preferred)) {
          usedBorderStyles.add(preferred);
          return preferred;
     }
     for (const style of BORDER_STYLE_POOL) {
          if (!usedBorderStyles.has(style)) {
               usedBorderStyles.add(style);
               return style;
          }
     }

     const fallback = existing || `border_${rule.id || Date.now()}`;
     usedBorderStyles.add(fallback);
     return fallback;
}

function booleansToStockEnums(stockArray) {
     if (!Array.isArray(stockArray)) return [];

     const containsEnum = stockArray.some((entry) => typeof entry === "string");
     if (containsEnum) {
          return stockArray
               .map((entry) => String(entry).toUpperCase())
               .filter((value) => value === "GREEN" || value === "YELLOW" || value === "RED");
     }

     const enums = [];
     if (stockArray[STOCK_INDEX_GREEN]) enums.push("GREEN");
     if (stockArray[STOCK_INDEX_YELLOW]) enums.push("YELLOW");
     if (stockArray[STOCK_INDEX_RED]) enums.push("RED");
     return enums;
}

const CONDITION_SHOP_ATTRIBUTE_KEYS = [
     { label: "Hersteller", attributeType: "string" },
     { label: "Onlineshop", attributeType: "string" },
     { label: "Preis (ohne Versandkosten)", attributeType: "numeric" },
     { label: "Gesamtpreis (mit Versandkosten)", attributeType: "numeric" },
     { label: "Versandkosten", attributeType: "numeric" },
     { label: "Anzahl Bewertungen", attributeType: "numeric" }
];

const CONDITION_OPERATOR_REGEX = [
     { symbol: ">=", regex: /.+(>=)[0-9.]+$/ },
     { symbol: "<=", regex: /.+(<=)[0-9.]+$/ },
     { symbol: ">", regex: /.+(>)[0-9.]+$/ },
     { symbol: "<", regex: /.+(<)[0-9.]+$/ },
     { symbol: "=", regex: /.+(=).+$/ }
];

function detectConditionOperator(token) {
     const text = String(token);
     if (/\sIN\s/i.test(text)) return "IN";
     if (/\sCONTAINS\s/i.test(text)) return "CONTAINS";
     for (const candidate of CONDITION_OPERATOR_REGEX) {
          if (candidate.regex.test(text)) return candidate.symbol;
     }
     return null;
}

function parseConditionAttributeToken(text) {
     const inMatch = text.match(/^(.+?)\s+IN\s+(.+)$/i);
     if (inMatch) {
          return { key: inMatch[1].trim(), operator: "IN", value: inMatch[2].trim(), attributeType: "tag" };
     }
     const containsMatch = text.match(/^(.+?)\s+CONTAINS\s+(.+)$/i);
     if (containsMatch) {
          return { key: containsMatch[1].trim(), operator: "CONTAINS", value: containsMatch[2].trim(), attributeType: "string" };
     }

     const operatorSymbol = detectConditionOperator(text);
     if (!operatorSymbol) return null;

     const index = text.indexOf(operatorSymbol);
     const key = text.substring(0, index);
     const rawValue = text.substring(index + operatorSymbol.length);
     const isQuoted = rawValue.startsWith("'") && rawValue.endsWith("'");
     const value = isQuoted ? rawValue.replace(/^'|'$/g, "") : rawValue;
     const known = CONDITION_SHOP_ATTRIBUTE_KEYS.find((a) => a.label === key);
     let attributeType = known ? known.attributeType : (isQuoted ? "string" : "numeric");
     if (!known && !isQuoted && operatorSymbol === "=") {
          attributeType = "tag";
     }
     return { key, operator: operatorSymbol, value, attributeType };
}

function equationToConditionTokens(equation) {
     if (!Array.isArray(equation)) return [];
     const tokens = [];
     equation.forEach((term) => {
          if (!term) return;
          if (term.type === "operator" || term.type === "bracket") {
               tokens.push(String(term.value));
               return;
          }
          if (term.type === "attribute") {
               const attr = term.shopAttribute || term.importedAttribute;
               const key = attr?.label ?? attr?.value ?? "";
               const attrType = term.shopAttribute?.attributeType ?? term.importedAttribute?.type;
               const operatorSymbol = term.operator?.value ?? term.operator?.label ?? "=";
               if (attrType === "string") {
                    if (operatorSymbol === "IN") {
                         tokens.push(`${key} IN ${term.value}`);
                    } else if (operatorSymbol === "CONTAINS") {
                         tokens.push(`${key} CONTAINS ${term.value}`);
                    } else {
                         tokens.push(`${key}='${term.value}'`);
                    }
               } else if (attrType === "tag") {
                    if (operatorSymbol === "IN") {
                         tokens.push(`${key} IN ${term.value}`);
                    } else if (operatorSymbol === "CONTAINS") {
                         tokens.push(`${key} CONTAINS ${term.value}`);
                    } else {
                         tokens.push(`${key}=${term.value}`);
                    }
               } else {
                    tokens.push(`${key}${operatorSymbol}${term.value}`);
               }
          }
     });
     return tokens;
}

function conditionTokensToEquation(tokens) {
     if (!Array.isArray(tokens)) return [];
     return tokens.map((token) => {
          const text = String(token);
          if (text === "(" || text === ")") return { type: "bracket", value: text };
          if (text === "AND" || text === "OR") return { type: "operator", value: text };

          const parsed = parseConditionAttributeToken(text);
          if (parsed) {
               const known = CONDITION_SHOP_ATTRIBUTE_KEYS.find((a) => a.label === parsed.key);
               const attrObj = {
                    value: parsed.key,
                    label: parsed.key,
                    attributeType: known ? known.attributeType : parsed.attributeType,
                    type: known ? known.attributeType : parsed.attributeType
               };
               return {
                    type: "attribute",
                    shopAttribute: known ? attrObj : null,
                    importedAttribute: known ? null : attrObj,
                    operator: { value: parsed.operator, label: parsed.operator },
                    value: parsed.value
               };
          }

          return { type: "attribute", shopAttribute: null, importedAttribute: null, operator: null, value: text };
     });
}

function serializeConditions(condition) {
     if (!condition) return [];
     if (Array.isArray(condition)) {
          if (condition.length === 0) return [];
          if (typeof condition[0] === "string") return condition;
          return equationToConditionTokens(condition);
     }
     if (condition.equation) return equationToConditionTokens(condition.equation);
     return [];
}

function normalizeDateForBackend(dateValue) {
     if (!dateValue) return null;
     let dateStr = String(dateValue);
     dateStr = dateStr.split('+')[0];
     dateStr = dateStr.split('Z')[0];
     dateStr = dateStr.split('.')[0];
     return dateStr;
}

function convertRotationToBackendEnum(frontendValue) {
     const mapping = {
          "WOECHENTLICH": "WEEKLY",
          "MONATLICH": "MONTHLY",
          "JAEHRLICH": "YEAR"
     };
     return mapping[frontendValue] || frontendValue;
}

function convertRotationFromBackendEnum(backendValue) {
     const mapping = {
          "WEEKLY": "WOECHENTLICH",
          "MONTHLY": "MONATLICH",
          "YEAR": "JAEHRLICH"
     };
     return mapping[backendValue] || backendValue;
}

const WEEKDAY_NAMES_BY_ORDINAL = ["MONTAG", "DIENSTAG", "MITTWOCH", "DONNERSTAG", "FREITAG", "SAMSTAG", "SONNTAG"];

function weekdayOrdinalsToNames(weekdays) {
     if (!Array.isArray(weekdays)) return [];
     return weekdays
          .map(day => (typeof day === "number" ? WEEKDAY_NAMES_BY_ORDINAL[day] : day))
          .filter(name => WEEKDAY_NAMES_BY_ORDINAL.includes(name));
}

function weekdayNamesToOrdinals(weekdays) {
     if (!Array.isArray(weekdays)) return [];
     return weekdays
          .map(name => (typeof name === "number" ? name : WEEKDAY_NAMES_BY_ORDINAL.indexOf(name)))
          .filter(ordinal => ordinal >= 0);
}

function monthdaysToInts(monthdays) {
     if (!Array.isArray(monthdays)) return [];
     return monthdays
          .map(day => (typeof day === "number" ? day : parseInt(day, 10)))
          .filter(day => Number.isInteger(day));
}

function monthdaysToStrings(monthdays) {
     if (!Array.isArray(monthdays)) return [];
     return monthdays.map(day => day.toString());
}

function convertModalRoundingTypeToServerFormat(modalType) {
     switch (modalType) {
          case "AB_HAELFTE_AUF": return "UPORDOWN";
          case "IMMER_AUFRUNDEN": return "UP";
          case "IMMER_ABRUNDEN": return "DOWN";
          default: return modalType;
     }
}

function resolveChannelForSave(editorProperties, group) {
     const editorChannel = editorProperties.channel
     if (editorChannel?.id != null) {
          return editorChannel
     }
     const groupChannel = group.channel
     if (groupChannel?.id != null) {
          return groupChannel
     }
     return null
}

class RuleStateService {
     static async merge(
          editorProperties,
          productRuleHelper,
          foreignShopRuleHelper,
          actionRuleHelper
     ) {
          const auth = PPUtils.getLogin();
          const customerId = PPUtils.getEffectiveCustomerId(auth);
          if (!PPUtils.isValidCustomerId(customerId)) {
               throw new Error("Bitte zuerst einen Kunden in der Seitenleiste wählen.");
          }
          if (auth?.user?.id == null) {
               throw new Error("Kein gültiger Benutzer für das Speichern der Regel.");
          }
          const currentUser = {
               id: auth.user.id,
               username: auth.user.username,
               name: auth.user.name,
               ruleengineAccess: auth.user.ruleengineAccess,
               customerId,
               configShopsActivated: PPUtils.getEffectiveConfigShopsActivated(auth)
          };

          const componentsString = buildComponentsString(
               productRuleHelper,
               foreignShopRuleHelper,
               actionRuleHelper,
               editorProperties
          );

          let mergedComponentsString = componentsString;
          if (editorProperties.__rawComponents) {
               let rebuiltComponents;
               try {
                    rebuiltComponents = JSON.parse(componentsString);
               } catch (error) {
                    throw new Error("Die Regel-Komponenten konnten nicht erzeugt werden (ungültiges JSON). Die Regel wurde NICHT gespeichert.");
               }
               const merged = sanitizeComponentsForBackend(
                    mergeComponentsPreservingUnknown(
                         editorProperties.__rawComponents,
                         rebuiltComponents
                    )
               );
               mergedComponentsString = JSON.stringify(merged);
          }

          // Pre-send guard: never transmit malformed component JSON.
          try {
               const parsedGuard = JSON.parse(mergedComponentsString);
               if (!isPlainObject(parsedGuard)) {
                    throw new Error("not-an-object");
               }
          } catch (error) {
               throw new Error("Die Regel-Komponenten sind ungültig und wurden NICHT gespeichert, um eine Beschädigung der Regel zu verhindern.");
          }

          const additionalJsonInfo = JSON.stringify({
               creator: currentUser.name,
               createDate: new Date().toLocaleDateString("de-DE", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
               })
          });

          const isUpdate = editorProperties.id !== undefined && editorProperties.id !== null;

          const foreignShopRules = foreignShopRuleHelper.getActiveRules();
          const hasForeignShopComponents = foreignShopRules.some(rule =>
               rule.keyString === "priceTracking" || rule.keyString === "foreignShopstock"
          );

          const group = editorProperties.group;
          const groupId = group?.id
          if (group == null || groupId == null || groupId === "") {
               throw new Error("Bitte wählen Sie eine Gruppe aus, bevor Sie die Regel speichern.");
          }

          const channel = resolveChannelForSave(editorProperties, group);
          if (channel == null) {
               throw new Error("Bitte wählen Sie einen Kanal aus, bevor Sie die Regel speichern.");
          }

          const channelId = channel.id;
          const channelName = channel.channelName;
          const ruleGroup = {
               id: Number(groupId),
               groupName: group.groupName,
               customerId,
               channel: {
                    id: channelId,
                    channelName: channelName,
                    hidden: false
               }
          };

          const isBackupRule = Boolean(editorProperties.isBackupRule);
          const existingEnabled = Boolean(editorProperties.__rawRule?.enabled);
          let ruleEnabled = false
          if (isBackupRule) {
               ruleEnabled = true
          } else if (isUpdate) {
               ruleEnabled = existingEnabled
          }

          if (!isBackupRule) {
               const priority = editorProperties.priority
               const hasPriority = typeof priority === "number" && Number.isInteger(priority) && priority >= 1
               if (!hasPriority) {
                    throw new Error("Bitte wählen Sie eine Priorität aus, bevor Sie die Regel speichern.")
               }
          }

          const ruleData = {
               userId: currentUser.id,
               customerId,
               additionalJsonInfo: additionalJsonInfo,
               lang: null,
               rule: {
                    ruleGroup: ruleGroup,
                    rulePriority: isBackupRule ? null : editorProperties.priority,
                    name: editorProperties.name || "",
                    enabled: ruleEnabled,
                    description: editorProperties.description || "",
                    version: isUpdate ? (editorProperties.version || 0) : 0,
                    foreignShopRule: hasForeignShopComponents,
                    deleted: false,
                    exceptionRule: editorProperties.sepcialRule || false,
                    backupRuleId: editorProperties.backupRule?.id || null,
                    parentRuleId: editorProperties.parentRuleId ?? null,
                    isBackupRule: editorProperties.isBackupRule || false,
                    customerId,
                    components: mergedComponentsString,
                    channel: {
                         id: channelId,
                         channelName: channelName,
                         hidden: false
                    },
                    creatorUser: editorProperties.creatorUser || currentUser,
                    lastEditUser: currentUser
               }
          };

          if (!isUpdate) {
               ruleData.rule.createDate = normalizeDateForBackend(new Date().toISOString());
          } else {
               if (editorProperties.createDate) {
                    ruleData.rule.createDate = normalizeDateForBackend(editorProperties.createDate);
               }
          }

          if (ruleData.rule.createDate) {
               ruleData.rule.createDate = normalizeDateForBackend(ruleData.rule.createDate);
          }

          if (isUpdate && !ruleData.rule.createDate && editorProperties.createDate) {
               ruleData.rule.createDate = normalizeDateForBackend(editorProperties.createDate);
          }

          if (ruleData.rule.createDate) {
               ruleData.rule.createDate = normalizeDateForBackend(ruleData.rule.createDate);
          }

          // G1 corruption-safety: on update, start from the ORIGINAL rule loaded
          // from the backend and override only the fields the editor truly owns.
          // This preserves creatorUser, createDate, parentRuleId, isBackupRule,
          // enabled, deleted and any backend field the React UI does not model.
          if (isUpdate && editorProperties.__rawRule) {
               const rawRule = editorProperties.__rawRule;
               ruleData.rule = {
                    ...rawRule,
                    ruleGroup: ruleGroup,
                    rulePriority: isBackupRule ? null : editorProperties.priority,
                    name: editorProperties.name ?? rawRule.name ?? "",
                    description: editorProperties.description ?? rawRule.description ?? "",
                    exceptionRule: editorProperties.sepcialRule || false,
                    backupRuleId: editorProperties.backupRule?.id ?? rawRule.backupRuleId ?? null,
                    foreignShopRule: hasForeignShopComponents,
                    enabled: ruleEnabled,
                    channel: {
                         id: channelId,
                         channelName: channelName,
                         hidden: false
                    },
                    components: mergedComponentsString,
                    version: editorProperties.version ?? rawRule.version ?? 0,
                    lastEditUser: currentUser,
                    id: editorProperties.id
               };
          }

          try {
               if (isUpdate) {
                    ruleData.rule.id = editorProperties.id;
                    return await RuleEngineBackend.updateRule(auth.token, ruleData);
               } else {
                    return await RuleEngineBackend.createRule(auth.token, ruleData);
               }
          } catch (error) {
               console.error("Error saving rule:", error);
               console.error("Rule data that failed:", JSON.stringify(ruleData, null, 2));
               throw error;
          }
     }

     static split(
          ruleObject,
          productRuleHelper,
          foreignShopRuleHelper,
          actionRuleHelper
     ) {
          if (ruleObject == null || typeof ruleObject !== "object" || Array.isArray(ruleObject)) {
               throw new Error("Regel konnte nicht geladen werden.")
          }

          const hasPriority = typeof ruleObject.rulePriority === "number" && Number.isInteger(ruleObject.rulePriority)
          const properties = {
               id: ruleObject.id,
               version: ruleObject.version,
               userId: ruleObject.creatorUser?.id,
               customerId: ruleObject.customerId,
               name: typeof ruleObject.name === "string" ? ruleObject.name : "",
               description: typeof ruleObject.description === "string" ? ruleObject.description : "",
               priority: hasPriority ? ruleObject.rulePriority : null,
               isBackupRule: Boolean(ruleObject.isBackupRule),
               sepcialRule: Boolean(ruleObject.exceptionRule),
               factfinder: false, triggerRule: false,
               shippingCost: false,
               group: ruleObject.ruleGroup ?? null,
               channel: ruleObject.channel ?? ruleObject.ruleGroup?.channel ?? null,
               backupRule: PPUtils.isPositiveIntId(ruleObject.backupRuleId) ? { id: ruleObject.backupRuleId } : null,
               createDate: ruleObject.createDate || null,
               creatorUser: ruleObject.creatorUser || null
          };

          let components = {};
          if (ruleObject.components == null || ruleObject.components === "") {
               components = {};
          } else if (typeof ruleObject.components === "object" && !Array.isArray(ruleObject.components)) {
               components = ruleObject.components;
          } else if (typeof ruleObject.components === "string") {
               const parsedComponents = JSON.parse(ruleObject.components);
               if (parsedComponents == null || typeof parsedComponents !== "object" || Array.isArray(parsedComponents)) {
                    throw new Error("Die Regel-Komponenten sind ungültig.");
               }
               components = parsedComponents;
          } else {
               throw new Error("Die Regel-Komponenten sind ungültig.");
          }

          // G1 corruption-safety: keep pristine copies of the loaded rule and its
          // parsed components so merge() can deep-merge edits onto them and never
          // drop backend fields the React editor does not model.
          properties.__rawRule = deepClone(ruleObject);
          properties.__rawComponents = deepClone(components);

          if (components.useAllPricesFromGroup) {
               foreignShopRuleHelper.UseAllPricesFromGroupHelper.setActive(true);
               foreignShopRuleHelper.UseAllPricesFromGroupHelper.setGroupName(
                    components.useAllPricesFromGroup.groupName || ""
               );
          }

          if (components.creator) {
               properties.componentsCreator = components.creator;
          }
          if (components.createDate) {
               properties.componentsCreateDate = components.createDate;
          }

          if (components.checkboxes) {
               properties.factfinder = components.checkboxes?.factFinder || false;
               properties.triggerRule = components.checkboxes?.allowTriggerRule || false;
               properties.shippingCost = components.checkboxes?.useDeliveryCosts || false;
          }

          if (components.channel) {
               productRuleHelper.LanguageAndChannelHelper.toggleLanguageAndChannel(true);

               const convertLanguageToSelectFormat = (lang) => {
                    const value = scalarString(lang, "de");
                    return { ...(isPlainObject(lang) ? lang : {}), label: value, value };
               };

               const convertChannelToSelectFormat = (channel) => {
                    const value = isPlainObject(channel)
                         ? channel.value ?? channel.channelName ?? channel.label ?? "Onlineshop"
                         : channel;
                    return {
                         ...(isPlainObject(channel) ? channel : {}),
                         label: String(isPlainObject(channel) ? channel.label ?? channel.channelName ?? value : value),
                         value
                    };
               };

               const languageValue = components.channel.lang
               if (languageValue != null && languageValue !== "") {
                    productRuleHelper.LanguageAndChannelHelper.setLanguage(convertLanguageToSelectFormat(languageValue));
               }
               const channelType = components.channel.type
               if (channelType != null && channelType !== "") {
                    productRuleHelper.LanguageAndChannelHelper.setChannel(convertChannelToSelectFormat(channelType));
               }
          }

          if (components.productAttributes) {
               productRuleHelper.ProductAttributeHelper.toggleProductAttribute(true);
               if (Array.isArray(components.productAttributes)) {
                    productRuleHelper.ProductAttributeHelper.setEquation(components.productAttributes);
               } else {
                    const equationArray = components.productAttributes ? [components.productAttributes] : [];
                    productRuleHelper.ProductAttributeHelper.setEquation(equationArray);
               }
          }

          const borderStyleToStockRuleId = new Map();

          if (components.own_stock && components.own_stock.length > 0) {
               components.own_stock.forEach(stockComponent => {
                    const stockRuleId = productRuleHelper.StockHelper.addStockRule({
                         stock: normalizeStockToBooleans(stockComponent.stock),
                         border_style: stockComponent.border_style || ""
                    });

                    if (stockComponent.border_style) {
                         borderStyleToStockRuleId.set(stockComponent.border_style, stockRuleId);
                    }
               });
          }

          if (components.product_filter) {
               productRuleHelper.RankingAndFilterHelper.toggleRankingAndFilter(true);
               productRuleHelper.RankingAndFilterHelper.setRankingFirst(toNullableNumber(components.product_filter.ranking?.greater_than));
               productRuleHelper.RankingAndFilterHelper.setRankingSecond(toNullableNumber(components.product_filter.ranking?.less_than));
               productRuleHelper.RankingAndFilterHelper.setPriceFirst(toNullableNumber(components.product_filter.price?.greater_than));
               productRuleHelper.RankingAndFilterHelper.setPriceSecond(toNullableNumber(components.product_filter.price?.less_than));
          }

          if (components.categories) {
               productRuleHelper.ProductCategoryHelper.toggleProductCategory(true);
               productRuleHelper.ProductCategoryHelper.setDepartment(csvToSelectOptions(components.categories.departments));
               productRuleHelper.ProductCategoryHelper.setArea(csvToSelectOptions(components.categories.groups));
               productRuleHelper.ProductCategoryHelper.setCategory(csvToSelectOptions(components.categories.categories));
               productRuleHelper.ProductCategoryHelper.setArticle(csvToSelectOptions(components.categories.products, "productnr"));
               const producer = components.categories.producer;
               productRuleHelper.ProductCategoryHelper.setProvider(
                    producer && String(producer).trim() !== "" && String(producer).trim().toUpperCase() !== "DEFAULT"
                         ? csvToSelectOptions(producer, "name")
                         : []
               );
               productRuleHelper.ProductCategoryHelper.setProductNrList(components.categories.productnrList || "");
               productRuleHelper.ProductCategoryHelper.setFilterProductNr(!(components.categories.exclude_given_products || false));
          }

          if (components.shops) {
               foreignShopRuleHelper.PriceTrackingHelper.setActive(true);
               const deliveryExcluded =
                    components.shops.deliveryCostAsCriteriaForExclusion ??
                    components.shops.excludeShopIfDeliveryCostAreNotAvailable ??
                    false;
               foreignShopRuleHelper.PriceTrackingHelper.setExcludeShopIfDeliveryCostAreNotAvailable(deliveryExcluded);
               foreignShopRuleHelper.PriceTrackingHelper.setUseAllProviders(components.shops.useAllProviders || false);

               const shopKeysWithPrio =
                    components.shops.shopsAndPrio ||
                    components.shops.shopKeysWithPrio ||
                    {};
               foreignShopRuleHelper.PriceTrackingHelper.setShopKeysWithPrio(shopKeysWithPrio);

               if (components.shops.providerFilter) {
                    const pf = components.shops.providerFilter;
                    const filterId = pf.providerFilterId ?? pf.filter_id ?? -1;
                    let filterValues = pf.providerFilterValues ?? pf.filter_values ?? "";
                    if (filterValues && typeof filterValues === "object") {
                         filterValues = JSON.stringify(filterValues);
                    }
                    foreignShopRuleHelper.PriceTrackingHelper.setProviderFilter({
                         filter_id: filterId,
                         filter_values: filterValues || ""
                    });
               }
          }

          const borderStyleToForeignStockRuleId = new Map();

          if (components.foreignshop_stock && components.foreignshop_stock.length > 0) {
               components.foreignshop_stock.forEach(stockComponent => {
                    let stockRuleReference = null;
                    if (stockComponent.border_style && borderStyleToStockRuleId.has(stockComponent.border_style)) {
                         stockRuleReference = borderStyleToStockRuleId.get(stockComponent.border_style);
                    }

                    const foreignStockRuleId = foreignShopRuleHelper.ForeignShopStockHelper.addForeignShopStockRule({
                         stock: normalizeStockToBooleans(stockComponent.stock),
                         border_style: stockComponent.border_style || "",
                         foreignshop_stock: stockComponent.foreignshop_stock || [{}],
                         stockRuleReference: stockRuleReference
                    });

                    if (stockComponent.border_style) {
                         borderStyleToForeignStockRuleId.set(stockComponent.border_style, foreignStockRuleId);
                    }
               });
          }

          if (components.maxPriceAdjustment) {
               actionRuleHelper.MaxPriceChangeHelper.toggle(true);
               actionRuleHelper.MaxPriceChangeHelper.setMaxPriceDecreaseValue(finiteNumberOr(components.maxPriceAdjustment.maxPriceDecreaseValue));
               actionRuleHelper.MaxPriceChangeHelper.setMaxPriceIncreaseValue(finiteNumberOr(components.maxPriceAdjustment.maxPriceIncreaseValue));
               applyMinimumBoundaryFromComponents(
                    actionRuleHelper.MaxPriceChangeHelper,
                    components.maxPriceAdjustment.minimumEuroOverEK,
                    components.maxPriceAdjustment.minimumPercent
               );
               actionRuleHelper.MaxPriceChangeHelper.setMinFormulaBase(scalarString(components.maxPriceAdjustment.minimumPriceFormulaBase));
               actionRuleHelper.MaxPriceChangeHelper.setMaxFormulaBase(scalarString(components.maxPriceAdjustment.maximumPriceFormulaBase));
               actionRuleHelper.MaxPriceChangeHelper.setMinFormulaAddTax(components.maxPriceAdjustment.minimumPriceFormulaAddTax || false);
               actionRuleHelper.MaxPriceChangeHelper.setMaxFormulaAddTax(components.maxPriceAdjustment.maximumPriceFormulaAddTax || false);

               const convertOpToSymbol = (op) => {
                    switch (op) {
                         case "PLUS": return "+";
                         case "MINUS": return "-";
                         case "MULTIPLICATION": return "*";
                         case "DIVISION": return "/";
                         default: return op;
                    }
               };

               const safeParseFloat = (value) => {
                    const parsed = parseFloat(value);
                    return isNaN(parsed) ? 0 : parsed;
               };

               const minimumPrice = components.maxPriceAdjustment.minimumPrice || {};
               actionRuleHelper.MaxPriceChangeHelper.setMinimumPrice({
                    OP_1: convertOpToSymbol(minimumPrice.OP_1) || null,
                    X: minimumPrice.X !== undefined ? safeParseFloat(minimumPrice.X) : 0,
                    OP_2: convertOpToSymbol(minimumPrice.OP_2) || null,
                    Y: minimumPrice.Y !== undefined ? safeParseFloat(minimumPrice.Y) : 0,
                    OP_3: convertOpToSymbol(minimumPrice.OP_3) || null,
                    Z: minimumPrice.Z !== undefined ? safeParseFloat(minimumPrice.Z) : 0,
                    useFactor: minimumPrice.useFactor || false
               });

               const maximumPrice = components.maxPriceAdjustment.maximumPrice || {};
               actionRuleHelper.MaxPriceChangeHelper.setMaximumPrice({
                    OP_1: convertOpToSymbol(maximumPrice.OP_1) || null,
                    X: maximumPrice.X !== undefined ? safeParseFloat(maximumPrice.X) : 0,
                    OP_2: convertOpToSymbol(maximumPrice.OP_2) || null,
                    Y: maximumPrice.Y !== undefined ? safeParseFloat(maximumPrice.Y) : 0,
                    OP_3: convertOpToSymbol(maximumPrice.OP_3) || null,
                    Z: maximumPrice.Z !== undefined ? safeParseFloat(maximumPrice.Z) : 0,
                    useFactor: maximumPrice.useFactor || false
               });
          }

          if (components.priceAdjustments && components.priceAdjustments.length > 0) {

               components.priceAdjustments.forEach(adjustment => {
                    // A price adjustment's border_style references an OWN stock (the backend
                    // resolves both the own-stock link and the matching foreign-stock link from
                    // this same border_style). We resolve it to the matching FOREIGN-shop stock
                    // id so the drawn connector line goes ForeignShop → PriceChange (the chain
                    // is OwnStock → ForeignShop → PriceChange). On save, we traverse back:
                    // foreignShopStockRuleReference → foreignShop.stockRuleReference → own-stock
                    // border_style.
                    let foreignShopStockRuleReference = null;
                    if (adjustment.border_style && borderStyleToForeignStockRuleId.has(adjustment.border_style)) {
                         foreignShopStockRuleReference = borderStyleToForeignStockRuleId.get(adjustment.border_style);
                    }

                    let formulas;
                    const adjustmentValues = adjustment.adjustmentValues;
                    if (Array.isArray(adjustmentValues)) {
                         formulas = adjustmentValues.map((item) => {
                              const conditionTokens = Array.isArray(item.ruleConditions) ? item.ruleConditions : [];
                              return {
                                   priceType: item.undercutTotalPrice
                                        ? "TOTAL_PRICE_WITH_SHIPPING"
                                        : "PRICE_WITHOUT_SHIPPING",
                                   underBidAmount: toNullableNumber(item.undercuttingValue),
                                   undercutType: item.undercutType || (adjustment.isEuro ? "€" : "%"),
                                   condition: conditionTokens.length > 0
                                        ? { equation: conditionTokensToEquation(conditionTokens), description: conditionTokens.join(" ") }
                                        : null
                              };
                         });
                    } else {
                         formulas = Object.entries(adjustmentValues || {}).map(([priceUntil, underBidAmount]) => {
                              const normalized = isPlainObject(underBidAmount)
                                   ? underBidAmount
                                   : { value: underBidAmount, type: adjustment.isEuro ? "€" : "%" };
                              return {
                                   priceUntil: toNullableNumber(priceUntil),
                                   underBidAmount: toNullableNumber(normalized.value),
                                   undercutType: normalized.type
                              };
                         });
                    }

                    const convertAdjustmentTypeToSelectFormat = (adjustmentType) => {
                         if (!adjustmentType) return null;
                         adjustmentType = canonicalPriceAdjustmentType(adjustmentType);

                         const comparisonOptions = {
                              "CHEAPEST_FS_PREIS": {
                                   value: "CHEAPEST_FS_PREIS",
                                   label: "günstigster Preis",
                                   formulaText: "günstigsten Preis"
                              },
                              "SECOND_CHEAPEST_PRICE": {
                                   value: "SECOND_CHEAPEST_PRICE",
                                   label: "zweitgünstigster Preis",
                                   formulaText: "zweitgünstigsten Preis"
                              },
                              "THIRD_CHEAPEST_PRICE": {
                                   value: "THIRD_CHEAPEST_PRICE",
                                   label: "drittgünstigster Preis",
                                   formulaText: "drittgünstigsten Preis"
                              },
                              "FOURTH_CHEAPEST_PRICE": {
                                   value: "FOURTH_CHEAPEST_PRICE",
                                   label: "viertgünstigster Preis",
                                   formulaText: "viertgünstigsten Preis"
                              },
                              "FIFTH_CHEAPEST_PRICE": {
                                   value: "FIFTH_CHEAPEST_PRICE",
                                   label: "fünftgünstigster Preis",
                                   formulaText: "fünftgünstigsten Preis"
                              },
                              "MOST_EXPENSIVE": {
                                   value: "MOST_EXPENSIVE",
                                   label: "teuerster Preis",
                                   formulaText: "teuersten Preis"
                              },
                              "AVERAGE_PRICE": {
                                   value: "AVERAGE_PRICE",
                                   label: "Durchschnittspreis",
                                   formulaText: "Durchschnittspreis"
                              },
                              "MEDIAN_PRICE": {
                                   value: "MEDIAN_PRICE",
                                   label: "Median",
                                   formulaText: "Median"
                              },
                              "NEXT_PRICE_ABOVE_MIN_PRICE": {
                                   value: "NEXT_PRICE_ABOVE_MIN_PRICE",
                                   label: "nächster Preis oberhalb des Mindestpreis (Exkl. Preisangleichungen)",
                                   formulaText: "nächsten Preis oberhalb des Mindestpreis (Exkl. Preisangleichungen)"
                              },
                              "NEXT_PRICE_ABOVE_MIN_PRICE_INCLUSIVE_UNDERCUTTING_VALUE": {
                                   value: "NEXT_PRICE_ABOVE_MIN_PRICE_INCLUSIVE_UNDERCUTTING_VALUE",
                                   label: "nächster Preis oberhalb des Mindestpreis (Inkl. Preisangleichungen)",
                                   formulaText: "nächsten Preis oberhalb des Mindestpreis (Inkl. Preisangleichungen)"
                              }
                         };

                         return comparisonOptions[adjustmentType] || null;
                    };

                    actionRuleHelper.PriceChangeHelper.addPriceAdjustmentRule({
                         active: true,
                         priceChangeAsCoupon: adjustment.isCoupon || false,
                         typeOfPriceChangeInEuro: resolvePriceAdjustmentIsEuro(adjustment, formulas),
                         ComparisonOption: convertAdjustmentTypeToSelectFormat(adjustment.adjustmentType),
                         action: formulas,
                         foreignShopStockRuleReference: foreignShopStockRuleReference,
                         border_style: adjustment.border_style || ""
                    });

               });
          }

          if (components.dateComponent) {
               actionRuleHelper.ChangeIntervalHelper.toggle(true);
               actionRuleHelper.ChangeIntervalHelper.setDates(backendDatesToHtml(components.dateComponent.dates));
               const backendTurnus = components.dateComponent.timePeriodTurnus || "";
               const frontendTurnus = convertRotationFromBackendEnum(backendTurnus);
               actionRuleHelper.ChangeIntervalHelper.setTimePeriodTurnus(frontendTurnus);
               actionRuleHelper.ChangeIntervalHelper.setWeekdays(weekdayOrdinalsToNames(components.dateComponent.weekdays || []));
               actionRuleHelper.ChangeIntervalHelper.setMonthdays(monthdaysToStrings(components.dateComponent.monthdays || []));
          }

          if (components.priceRounding) {
               actionRuleHelper.RoundPriceHelper.toggle(true);
               actionRuleHelper.RoundPriceHelper.setRoundingType(convertServerRoundingTypeToModalFormat(components.priceRounding.roundingType || ""));

               const convertedRoundingValues = convertServerRoundingValuesToModalFormat(components.priceRounding.roundingValues || {});
               actionRuleHelper.RoundPriceHelper.setRoundingValues(convertedRoundingValues);
               actionRuleHelper.RoundPriceHelper.setContraryBackupRule(components.priceRounding.activateContraryBackupRuleRoundMode || false);
          }

          if (components.calculate_by_channel) {
               actionRuleHelper.CalculationChannelAndRuleHelper.toggle(true);
               actionRuleHelper.CalculationChannelAndRuleHelper.setChannelId(components.calculate_by_channel.channel_id ?? null);
               actionRuleHelper.CalculationChannelAndRuleHelper.setRuleId(components.calculate_by_channel.rule_id ?? null);
          }

          if (components.adjustmentByMarge) {
               actionRuleHelper.PriceChangeMarginHelper.toggle(true);

               const convertServerMargeTypeToModalFormat = (serverType) => {
                    switch (serverType?.toLowerCase()) {
                         case "formel": return "FORMEL";
                         case "nettofaktor": return "NETTOFAKTOR";
                         case "bruttofaktor": return "BRUTTOFAKTOR";
                         default: return serverType?.toUpperCase() || "";
                    }
               };

               const convertServerMargeFallToModalFormat = (serverFall) => {
                    switch (serverFall?.toLowerCase()) {
                         case "all": return "ALL_PRODUCTS";
                         case "lesser": return "ONLY_LESSER";
                         case "greater": return "ONLY_GREATER";
                         default: return serverFall?.toUpperCase() || "";
                    }
               };

               actionRuleHelper.PriceChangeMarginHelper.setMargeType(convertServerMargeTypeToModalFormat(components.adjustmentByMarge.margeType));

               const parseFloatSafe = (value) => {
                    if (value === "NaN" || value === null || value === undefined) return 0;
                    const parsed = parseFloat(value);
                    return isNaN(parsed) ? 0 : parsed;
               };

               actionRuleHelper.PriceChangeMarginHelper.setNettoBruttoValue(parseFloatSafe(components.adjustmentByMarge.nettoBruttoValue));
               actionRuleHelper.PriceChangeMarginHelper.setMargeFall(convertServerMargeFallToModalFormat(components.adjustmentByMarge.MargeFall));
               applyMinimumBoundaryFromComponents(
                    actionRuleHelper.PriceChangeMarginHelper,
                    components.adjustmentByMarge.minimumEuroOverEK,
                    components.adjustmentByMarge.minimumPercent
               );
               actionRuleHelper.PriceChangeMarginHelper.setMinimumPriceFormulaBase(scalarString(components.adjustmentByMarge.minimumPriceFormulaBase));
               actionRuleHelper.PriceChangeMarginHelper.setMinimumPriceFormulaAddTax(components.adjustmentByMarge.minimumPriceFormulaAddTax || false);

               const formula = components.adjustmentByMarge.formula || {};
               const cleanFormula = {
                    OP_1: formula.OP_1 || null,
                    X: parseFloatSafe(formula.X),
                    OP_2: formula.OP_2 || null,
                    Y: parseFloatSafe(formula.Y),
                    OP_3: formula.OP_3 || null,
                    Z: parseFloatSafe(formula.Z),
                    useFactor: formula.useFactor || false
               };

               actionRuleHelper.PriceChangeMarginHelper.setFormula(cleanFormula);
          }

          if (components.amazonBuyBox) {
               actionRuleHelper.AmazonBuyBoxHelper.toggle(true);
          }

          return properties;
     }

     static splitAsTemplate(
          ruleObject,
          productRuleHelper,
          foreignShopRuleHelper,
          actionRuleHelper
     ) {
          const properties = RuleStateService.split(
               ruleObject,
               productRuleHelper,
               foreignShopRuleHelper,
               actionRuleHelper
          );

          const parentRuleId = ruleObject.id;

          delete properties.id;
          delete properties.version;
          delete properties.createDate;
          delete properties.creatorUser;
          delete properties.__rawRule;
          delete properties.__rawComponents;
          delete properties.componentsCreator;
          delete properties.componentsCreateDate;

          properties.parentRuleId = parentRuleId;
          properties.name = "";

          return properties;
     }
}

function buildComponentsString(productRuleHelper, foreignShopRuleHelper, actionRuleHelper, editorProperties) {
     const components = {};

     const productRules = productRuleHelper.getActiveRules();
     const foreignShopRules = foreignShopRuleHelper.getActiveRules();
     const actionRules = actionRuleHelper.getActiveRules();

     if (!productRules.some((rule) => rule.keyString === "languageAndChannel")
          && editorProperties?.channel) {
          const channelMeta = editorProperties.channel;
          components.channel = {
               lang: "de",
               type: channelMeta.channelName || channelMeta.name || "Onlineshop"
          };
     }

     const stockRuleIdToBorderStyle = new Map();
     const foreignStockRuleIdToBorderStyle = new Map();
     const usedBorderStyles = new Set();

     productRules.forEach(rule => {
          if (rule.keyString === "languageAndChannel") {
               components.channel = {
                    lang: typeof rule.language === 'object' ? (rule.language?.value || rule.language?.label || "de") : rule.language || "de",
                    type: typeof rule.channel === 'object' ? (rule.channel?.value || rule.channel?.label || "Onlineshop") : rule.channel || "Onlineshop"
               };
          }
          if (rule.keyString === "product-attribute" && rule.equation) {
               if (!components.productAttributes) components.productAttributes = [];
               if (Array.isArray(rule.equation)) {
                    components.productAttributes = [
                         ...components.productAttributes,
                         ...rule.equation
                    ];
               } else {
                    components.productAttributes.push(rule.equation);
               }
          }
          if (rule.keyString === "stock") {
               if (!components.own_stock) components.own_stock = [];
               const borderStyle = pickBorderStyle(rule, usedBorderStyles);
               components.own_stock.push({
                    stock: booleansToStockEnums(rule.stock),
                    border_style: borderStyle
               });
               if (rule.id) {
                    stockRuleIdToBorderStyle.set(rule.id, borderStyle);
               }
          }
          if (rule.keyString === "rankingAndFilter") {
               components.product_filter = {
                    ranking: {
                         greater_than: toNullableNumber(rule.rankingFirst),
                         less_than: toNullableNumber(rule.rankingSecond)
                    },
                    price: {
                         greater_than: toNullableNumber(rule.priceFirst),
                         less_than: toNullableNumber(rule.priceSecond)
                    }
               };
          }
          if (rule.keyString === "productCategory") {
               components.categories = {
                    departments: toBackendCsv(rule.department, ["catShort", "value", "label"]),
                    groups: toBackendCsv(rule.area, ["catShort", "value", "label"]),
                    categories: toBackendCsv(rule.category, ["catShort", "value", "label"]),
                    productnrList: rule.productNrList || "",
                    products: toBackendCsv(rule.article, ["productnr", "productNr", "value", "label"]),
                    producer: toBackendCsv(rule.provider, ["name", "label", "value"]),
                    exclude_given_products: !(rule.filterProductNr ?? true)
               };
          }
     });

     foreignShopRules.forEach(rule => {
          if (rule.keyString === "priceTracking") {
               const shopKeysWithPrio = rule.shopKeysWithPrio || {};
               const providerFilterPayload = {};
               const pf = rule.providerFilter || {};
               if (pf.filter_id && pf.filter_id !== -1) {
                    providerFilterPayload.filter_id = pf.filter_id;
               } else if (pf.filter_values) {
                    try {
                         providerFilterPayload.filter_values =
                              typeof pf.filter_values === "string" ? JSON.parse(pf.filter_values) : pf.filter_values;
                    } catch {
                         providerFilterPayload.filter_values = {};
                    }
               }
               components.shops = {
                    excludeShopIfDeliveryCostAreNotAvailable: rule.excludeShopIfDeliveryCostAreNotAvailable || false,
                    useAllProviders: rule.useAllProviders || false,
                    shopKeysWithPrio,
                    ...(Object.keys(providerFilterPayload).length > 0 ? { providerFilter: providerFilterPayload } : {})
               };
          }
          if (rule.keyString === "foreignShopstock") {
               // A foreign-shop stock MUST link to an existing own stock: the backend reads
               // border_style with no null guard and dereferences the resolved own-stock link
               // while parsing price adjustments. Resolve the reference to the linked own
               // stock's border_style; skip emitting an orphaned foreign stock (one without a
               // valid own-stock link) so we never produce JSON that breaks the backend.
               let borderStyle = null;

               if (rule.stockRuleReference) {
                    const referencedStockRuleId = typeof rule.stockRuleReference === 'object'
                         ? rule.stockRuleReference.id || rule.stockRuleReference
                         : rule.stockRuleReference;
                    const referencedBorderStyle = stockRuleIdToBorderStyle.get(referencedStockRuleId);
                    if (referencedBorderStyle !== undefined) {
                         borderStyle = referencedBorderStyle;
                    }
               }

               if (!borderStyle && rule.border_style &&
                    [...stockRuleIdToBorderStyle.values()].includes(rule.border_style)) {
                    borderStyle = rule.border_style;
               }

               if (borderStyle) {
                     if (!components.foreignshop_stock) components.foreignshop_stock = [];
                     components.foreignshop_stock.push({
                          stock: booleansToStockEnums(rule.stock),
                          border_style: borderStyle,
                          foreignshop_stock: rule.foreignshop_stock || [{}]
                     });
                     if (rule.id) {
                          foreignStockRuleIdToBorderStyle.set(rule.id, borderStyle);
                     }
                }
          }
          if (rule.keyString === "useAllPricesFromGroup") {
               components.useAllPricesFromGroup = {
                    groupName: rule.groupName || ""
               };
          }
     });

     actionRules.forEach(rule => {
          if (rule.keyString === "maxPriceAdjustment") {
               const minimumPrice = rule.minimumPrice
                    ? buildMargeFormulaPayload(rule.minimumPrice)
                    : {};

               const maximumPrice = rule.maximumPrice
                    ? buildMargeFormulaPayload(rule.maximumPrice)
                    : {};

               components.maxPriceAdjustment = {
                    maxPriceDecreaseValue: finiteString(rule.maxPriceDecreaseValue),
                    maxPriceIncreaseValue: finiteString(rule.maxPriceIncreaseValue),
                    minimumEuroOverEK: serializeMinimumBoundaryValue(rule.minimumEuroOverEK, rule.minimumBoundaryEnabled),
                    minimumPercent: serializeMinimumBoundaryValue(rule.minimumPercent, rule.minimumBoundaryEnabled),
                    minimumPriceFormulaBase: scalarString(rule.minimumPriceFormulaBase),
                    maximumPriceFormulaBase: scalarString(rule.maximumPriceFormulaBase),
                    minimumPriceFormulaAddTax: rule.minimumPriceFormulaAddTax || false,
                    maximumPriceFormulaAddTax: rule.maximumPriceFormulaAddTax || false,
                    minimumPrice: minimumPrice,
                    maximumPrice: maximumPrice
               };
          }
          if (rule.keyString === "priceChange") {
               if (!components.priceAdjustments) components.priceAdjustments = [];
               const hasExtendedFormulas = Array.isArray(rule.action) &&
                    rule.action.some((f) => f?.priceType != null);
               // The price adjustment links to a FOREIGN-shop stock rule via
               // foreignShopStockRuleReference. We traverse the chain backwards:
               // foreignShopStockRuleReference → foreignShop.stockRuleReference →
               // own-stock border_style. The backend reads border_style as the
               // foreign key to the own-stock component, so we must emit the
               // own-stock's border_style (not the foreign-shop's id).
               let borderStyle = null;
               if (rule.foreignShopStockRuleReference) {
                    const foreignStockBorderStyle = foreignStockRuleIdToBorderStyle.get(rule.foreignShopStockRuleReference);
                    if (foreignStockBorderStyle) {
                         borderStyle = foreignStockBorderStyle;
                    }
               }

               // Fallback: if the rule still carries a legacy stockRuleReference
               // (pointing directly at an own stock), resolve it directly.
               if (!borderStyle && rule.stockRuleReference) {
                    const refId = typeof rule.stockRuleReference === "object"
                         ? rule.stockRuleReference.id || rule.stockRuleReference
                         : rule.stockRuleReference;
                    const linkedStyle = stockRuleIdToBorderStyle.get(refId);
                    if (linkedStyle) {
                         borderStyle = linkedStyle;
                    }
               }
               let adjustmentValues;
               if (hasExtendedFormulas) {
                    adjustmentValues = (rule.action || [])
                         .filter((f) => toNullableNumber(f?.underBidAmount) !== null && f?.priceType != null)
                         .map((f) => ({
                              undercuttingValue: toNullableNumber(f.underBidAmount),
                              undercutTotalPrice: f.priceType === "TOTAL_PRICE_WITH_SHIPPING",
                              undercutType: f.undercutType || (rule.typeOfPriceChangeInEuro ? "€" : "%"),
                              ruleConditions: serializeConditions(f.condition)
                         }));
               } else {
                    adjustmentValues = (rule.action || []).reduce((acc, formula) => {
                         const priceUntil = toNullableNumber(formula.priceUntil);
                         const underBidAmount = toNullableNumber(formula.underBidAmount);
                         if (priceUntil !== null && underBidAmount !== null) {
                              const key = doubleKey(priceUntil);
                              if (key !== null) {
                                   acc[key] = {
                                        value: underBidAmount,
                                        type: formula.undercutType || (rule.typeOfPriceChangeInEuro ? "€" : "%")
                                   };
                              }
                         }
                         return acc;
                    }, {});
               }
               const adjustmentPayload = {
                    adjustmentValues,
                    isCoupon: rule.priceChangeAsCoupon || false,
                    isEuro: rule.typeOfPriceChangeInEuro || false
               };
               if (borderStyle) {
                    adjustmentPayload.border_style = borderStyle;
               }
               if (!hasExtendedFormulas) {
                    adjustmentPayload.adjustmentType =
                         typeof rule.ComparisonOption === "object"
                              ? canonicalPriceAdjustmentType(rule.ComparisonOption?.value || "")
                              : canonicalPriceAdjustmentType(rule.ComparisonOption || "");
               }
               components.priceAdjustments.push(adjustmentPayload);
          }
          if (rule.keyString === "changeInterval") {
               const frontendTurnus = rule.timePeriodTurnus || "";
               const backendTurnus = convertRotationToBackendEnum(frontendTurnus);
               components.dateComponent = {
                    dates: htmlDatesToBackend(rule.dates),
                    timePeriodTurnus: backendTurnus,
                    weekdays: weekdayNamesToOrdinals(rule.weekdays || []),
                    monthdays: monthdaysToInts(rule.monthdays || [])
               };
          }
          if (rule.keyString === "roundPrice") {
               const serverRoundingValues = convertModalRoundingValuesToServerFormat(rule.roundingValues || {});
               components.priceRounding = {
                    roundingType: convertModalRoundingTypeToServerFormat(rule.roundingType || ""),
                    roundingValues: serverRoundingValues,
                    activateContraryBackupRuleRoundMode: rule.activateContraryBackupRuleRoundMode ?? rule.contraryBackupRule ?? false
               };
          }
          if (rule.keyString === "calculationChannelAndRulePrice") {
               const channelId = rule.channel_id ?? rule.channelId;
               const ruleId = rule.rule_id ?? rule.ruleId;
               components.calculate_by_channel = { channel_id: channelId ?? null };
               if (ruleId !== null && ruleId !== undefined && ruleId !== 0) {
                    components.calculate_by_channel.rule_id = ruleId;
               }
          }
          if (rule.keyString === "priceChangeMargin") {
               const convertModalMargeTypeToServerFormat = (modalType) => {
                    switch (modalType?.toUpperCase()) {
                         case "FORMEL": return "formel";
                         case "NETTOFAKTOR": return "nettofaktor";
                         case "BRUTTOFAKTOR": return "bruttofaktor";
                         default: return modalType?.toLowerCase() || "";
                    }
               };

               const convertModalMargeFallToServerFormat = (modalFall) => {
                    switch (modalFall?.toUpperCase()) {
                         case "ALL_PRODUCTS": return "all";
                         case "ONLY_LESSER": return "lesser";
                         case "ONLY_GREATER": return "greater";
                         default: return modalFall?.toLowerCase() || "";
                    }
               };

               components.adjustmentByMarge = {
                    margeType: convertModalMargeTypeToServerFormat(rule.margeType),
                    nettoBruttoValue: finiteString(rule.nettoBruttoValue, "-1"),
                    MargeFall: convertModalMargeFallToServerFormat(rule.MargeFall),
                    minimumEuroOverEK: serializeMinimumBoundaryValue(rule.minimumEuroOverEK, rule.minimumBoundaryEnabled),
                    minimumPercent: serializeMinimumBoundaryValue(rule.minimumPercent, rule.minimumBoundaryEnabled),
                    minimumPriceFormulaBase: scalarString(rule.minimumPriceFormulaBase),
                    minimumPriceFormulaAddTax: rule.minimumPriceFormulaAddTax || false,
                    formula: buildMargeFormulaPayload(rule.formula)
               };
          }
          if (rule.keyString === "amazonBuyBox") {
               components.amazonBuyBox = {};
          }
     });

     const auth = PPUtils.getLogin();
     components.checkboxes = {
          factFinder: editorProperties.factfinder || false,
          useDeliveryCosts: editorProperties.shippingCost || false,
          allowTriggerRule: editorProperties.triggerRule || false
     };

     components.creator = editorProperties.componentsCreator || auth.user?.name || "Sylphen Admin";
     components.createDate = editorProperties.componentsCreateDate || new Date().toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
     }).replace(/\./g, '-').split('-').reverse().join('-'); //23-07-2025

     return JSON.stringify(sanitizeComponentsForBackend(components));
}

RuleStateService.buildComponentsString = buildComponentsString;

export default RuleStateService;